import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BadgeCheck, PencilLine, PhoneCall, ThumbsUp, type LucideIcon } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { MARKET_PROMOTION_PERMISSIONS } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { mpApprovalQueryOptions } from '../api/marketPromotion.api';
import { DonationDecisionForm } from '../components/DonationDecisionForm';
import { DonationDecisionTrail } from '../components/DonationDecisionTrail';
import { DonationDetailsCard } from '../components/DonationDetailsCard';
import { DonationEditForm } from '../components/DonationEditForm';
import { DonationSummaryPanel } from '../components/DonationSummaryPanel';
import { MisVerifyDialog } from '../components/MisVerifyDialog';
import { SummarySideLayout } from '../components/SummarySideLayout';
import {
  DONATION_DETAIL_ORIGIN,
  type DonationDecisionStep,
  type DonationDetailOrigin,
} from '../constants/marketPromotion.constants';
import { canDecideFrom, getDonationDecisionStep } from '../utils/donationDecision';

const DECISION_BUTTON: Record<DonationDecisionStep, { label: string; icon: LucideIcon }> = {
  recommend: { label: 'Recommend', icon: ThumbsUp },
  approve: { label: 'Approve', icon: BadgeCheck },
};

interface DecisionStepButtonProps {
  step: DonationDecisionStep;
  isOutline: boolean;
  isDisabled: boolean;
  onClick: () => void;
}

function DecisionStepButton({ step, isOutline, isDisabled, onClick }: DecisionStepButtonProps) {
  const { label, icon: Icon } = DECISION_BUTTON[step];
  return (
    <Button variant={isOutline ? 'outline' : 'default'} onClick={onClick} disabled={isDisabled}>
      <Icon className="size-4" aria-hidden />
      {label}
    </Button>
  );
}

interface DonationDetailPageProps {
  detailId: number;
  from: DonationDetailOrigin;
  step: DonationDecisionStep | undefined;
}

export function DonationDetailPage({ detailId, from, step }: DonationDetailPageProps) {
  const navigate = useNavigate();
  const goBack = useHistoryBack(() => void navigate({ to: from }));
  useRememberViewedRow('donations', detailId);
  const { can } = usePermissions();

  const { data: donation, error, refetch } = useQuery(mpApprovalQueryOptions(detailId));
  const isMisQueue = from === DONATION_DETAIL_ORIGIN.mis;
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const canDecide = canDecideFrom(from) && can(MARKET_PROMOTION_PERMISSIONS.APPROVAL_APPROVE);
  const availableStep = donation && canDecide ? getDonationDecisionStep(donation) : undefined;
  const openStep = step !== undefined && step === availableStep ? step : undefined;
  const isFormOpen = openStep !== undefined || isEditing;

  function showDecisionStep(nextStep: DonationDecisionStep | undefined) {
    void navigate({
      to: '/market-promotion/approvals/$detailId',
      params: { detailId },
      search: nextStep === undefined ? { from } : { from, step: nextStep },
      replace: true,
    });
  }

  const decisionButton =
    availableStep !== undefined && openStep === undefined ? (
      <DecisionStepButton
        step={availableStep}
        isOutline={isMisQueue}
        isDisabled={isEditing}
        onClick={() => showDecisionStep(availableStep)}
      />
    ) : null;

  let donationCard: ReactNode = <DonationDetailsCard donation={donation} />;
  if (donation && openStep !== undefined) {
    donationCard = (
      <DonationDecisionForm
        key={`${String(detailId)}-${openStep}`}
        step={openStep}
        donation={donation}
        onClose={() => showDecisionStep(undefined)}
        onDone={goBack}
      />
    );
  } else if (donation && isEditing) {
    donationCard = <DonationEditForm donation={donation} onClose={() => setIsEditing(false)} />;
  }

  return (
    <div className="flex flex-col gap-page-gap">
      <PageHeader
        title="Donation Details"
        leading={<BackButton onClick={goBack} />}
        actions={
          <>
            {decisionButton}
            {isMisQueue ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  disabled={donation === undefined || isFormOpen}
                >
                  <PencilLine className="size-4" aria-hidden />
                  Edit
                </Button>
                <Button onClick={() => setIsVerifyOpen(true)} disabled={donation === undefined}>
                  <PhoneCall className="size-4" aria-hidden />
                  {donation?.isMisVerified ? 'Update MIS Verify' : 'MIS Verify'}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this donation"
          onRetry={() => void refetch()}
        />
      ) : (
        <SummarySideLayout
          summary={<DonationSummaryPanel donation={donation} isMisQueue={isMisQueue} />}
        >
          {donationCard}
          {isFormOpen ? null : (
            <DonationDecisionTrail donation={donation} isMisQueue={isMisQueue} />
          )}
        </SummarySideLayout>
      )}

      {isVerifyOpen && donation ? (
        <MisVerifyDialog donation={donation} open={isVerifyOpen} onOpenChange={setIsVerifyOpen} />
      ) : null}
    </div>
  );
}
